import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/features/fraud/shared'
import { discountErrorMessage } from '@/lib/api/discount-client'
import {
  useCreateDiscountSchedule,
  useDiscountLocations,
  useDiscountPreview,
  useDiscountSchedule,
  useDiscountScope,
  useDiscountTargetItems,
  useUpdateDiscountSchedule,
} from '@/lib/api/discount-queries'
import type { DiscountPreviewInput, DiscountPreviewResponse, DiscountSchedule } from '@/lib/api/discount-types'
import { useEvent } from '@/lib/api/queries'
import type { Event, MenuItem } from '@/lib/api/types'
import { DEFAULT_EVENT_TIMEZONE, formatInEventZone } from '@/lib/eventTime'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { FieldError, Notice, RuleInput, Section } from './editor-fields'
import {
  changedFields,
  draftToInput,
  formatCurrency,
  freeItemIds,
  hasErrors,
  itemLocationId,
  locationLabel,
  locationVendor,
  mapServerErrors,
  menuUnits,
  newDraft,
  overridesOnly,
  scheduleToDraft,
  validateDraft,
  type DraftErrors,
  type DraftUpdate,
  type ErrorSlot,
  type MenuUnits,
  type ServerErrors,
} from './editor-model'
import { HINT, LABEL, fieldClass } from './editor-styles'
import { eventDatesFit } from './list-format'
import { PreviewPanel, type PreviewState } from './preview-panel'
import { TargetingSection, type PickerOption } from './targeting-pickers'
import { WindowSection } from './window-fields'

const PREVIEW_DEBOUNCE_MS = 600
const NO_ITEMS: MenuItem[] = []

/** The part of the form a change touches; a new draft shows its errors from then on. */
type TouchSlot = 'name' | 'defaultRule' | 'targets' | 'window'

function ModalFrame({ title, subtitle, onDismiss, footer, bodyRef, children }: {
  title: string
  subtitle?: string | null
  onDismiss: () => void
  footer?: ReactNode
  bodyRef?: Ref<HTMLDivElement>
  children: ReactNode
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onDismiss])
  useEffect(() => {
    document.body.classList.add('overflow-hidden')
    return () => document.body.classList.remove('overflow-hidden')
  }, [])
  // Take focus into the dialog: to the field marked `data-autofocus` if there
  // is one, else the dialog itself. (An effect rather than `autoFocus`, which
  // StrictMode's remount in development would undo.)
  const dialogRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog || dialog.contains(document.activeElement)) return
    const target = dialog.querySelector<HTMLElement>('[data-autofocus]') ?? dialog
    target.focus()
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onDismiss} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-editor-title"
        tabIndex={-1}
        className="relative flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl outline-none dark:border-neutral-800 dark:bg-neutral-900"
      >
        <header className="flex items-start justify-between gap-4 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800 sm:px-6">
          <div className="min-w-0">
            <h2 id="schedule-editor-title" className="truncate text-lg font-semibold text-neutral-900 dark:text-white">{title}</h2>
            {subtitle && <p className="truncate text-sm text-neutral-500 dark:text-neutral-400">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">{children}</div>
        {footer && <footer className="border-t border-neutral-200 px-4 py-3 dark:border-neutral-800 sm:px-6">{footer}</footer>}
      </div>
    </div>
  )
}

/**
 * A modal to create a schedule (`scheduleId` null) or to edit or view one.
 * An existing schedule is fetched afresh on every open and edited from that,
 * never from the list's copy. It opens read-only when the viewer may not edit
 * it (`can_edit`; always false once archived).
 */
export function ScheduleEditor({ eventId, scheduleId, name, onClose, onSaved }: {
  eventId: number
  scheduleId: number | null
  /** The name the list shows, for the title while the schedule loads. */
  name?: string
  onClose: () => void
  onSaved?: (schedule: DiscountSchedule) => void
}) {
  const { data: event, error } = useEvent(eventId)
  const scheduleQuery = useDiscountSchedule(scheduleId)
  // Data from before this open (the cache) is not good enough to edit from.
  const [openedAt] = useState(() => Date.now())
  const fresh = !!scheduleQuery.data && scheduleQuery.dataUpdatedAt >= openedAt
  const schedule = scheduleId ? (fresh ? scheduleQuery.data! : undefined) : null
  const scheduleError = scheduleId && !fresh && scheduleQuery.isFetchedAfterMount ? scheduleQuery.error : null

  // Hand focus back to what opened the editor (Edit, View, New) when it closes.
  const [opener] = useState(() => document.activeElement)
  useEffect(() => () => {
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
  }, [opener])

  if (!event || schedule === undefined) {
    const loadError = error
      ? `Could not load the event: ${discountErrorMessage(error)}`
      : scheduleError ? `Could not load the schedule: ${discountErrorMessage(scheduleError)}` : null
    return (
      <ModalFrame
        title={scheduleId ? 'Schedule' : 'New schedule'}
        subtitle={name}
        onDismiss={onClose}
        footer={loadError ? (
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          </div>
        ) : undefined}
      >
        {loadError ? (
          <Notice kind="error">{loadError}</Notice>
        ) : (
          <div role="status" aria-label="Loading" className="flex justify-center py-16"><Spinner /></div>
        )}
      </ModalFrame>
    )
  }
  return (
    <EditorForm
      key={schedule?.id ?? 'new'}
      eventId={eventId}
      event={event}
      schedule={schedule}
      onClose={onClose}
      onSaved={onSaved}
    />
  )
}

function unitsNote(units: MenuUnits): string {
  const perUnit = units.tokenValue ? formatCurrency(units.tokenValue, units.currency) : null
  if (units.eventType !== 'direct_pay') return `Amounts are in tokens${perUnit ? ` (1 token = ${perUnit})` : ''}, as on the menu.`
  if (units.tokenValue === 1) return `Amounts are in ${units.currency}, as on the menu.`
  return `Amounts are in menu units, as on the menu${perUnit ? ` (1 unit = ${perUnit})` : ''}.`
}

/** What still stands between the draft and Save, for the footer. */
function missingSummary(errors: DraftErrors): string[] {
  const missing: string[] = []
  if (errors.name) missing.push('a name')
  if (errors.defaultRule) missing.push('a valid default rule')
  if (errors.targets) missing.push('something to discount')
  if (Object.keys(errors.items).length) missing.push('valid item rules')
  if (errors.endsAt || errors.dailyTimes || errors.endDate) missing.push('a valid window')
  return missing
}

function EditorForm({ eventId, event, schedule, onClose, onSaved }: {
  eventId: number
  event: Event
  schedule: DiscountSchedule | null
  onClose: () => void
  onSaved?: (schedule: DiscountSchedule) => void
}) {
  const timeZone = event.timezone || DEFAULT_EVENT_TIMEZONE
  const units = useMemo(() => menuUnits(event), [event])
  const canEditDiscounts = usePermission('can_edit_discounts')
  // The backend decides: `can_edit` on the schedule (false once archived),
  // and the scope for what the pickers offer.
  const scopeQuery = useDiscountScope(eventId)
  const scope = scopeQuery.data
  const archived = !!schedule?.archived_at
  const readOnly = !canEditDiscounts || (!!schedule && !schedule.can_edit)

  const [initialDraft] = useState(() => (schedule ? scheduleToDraft(schedule, timeZone) : newDraft(units)))
  const [draft, setDraft] = useState(initialDraft)
  const [touched, setTouched] = useState<Partial<Record<TouchSlot, boolean>>>({})
  const patch = useCallback((update: DraftUpdate, slot: TouchSlot) => {
    setDraft((current) => ({ ...current, ...(typeof update === 'function' ? update(current) : update) }))
    setTouched((current) => (current[slot] ? current : { ...current, [slot]: true }))
  }, [])

  // ------------------------------------------------ vendors, locations, items
  const locationsQuery = useDiscountLocations(eventId)
  const itemsQuery = useDiscountTargetItems(eventId)
  const locations = locationsQuery.data
  const items = itemsQuery.data ?? NO_ITEMS
  // Below event level only the viewer's own vendors, locations and their
  // items are offered: the scope's ids, named from the locations when they
  // are there. If the scope can't be read, everything is offered and the
  // server refuses what isn't allowed.
  const allowed = scope && !scope.event_wide ? scope : null

  const lookups = useMemo(() => {
    const locationLabels = new Map<number, string>()
    const vendorOfLocation = new Map<number, number>()
    const vendorNames = new Map<number, string>()
    for (const location of locations ?? []) {
      locationLabels.set(location.id, locationLabel(location))
      const vendor = locationVendor(location)
      if (vendor) {
        vendorOfLocation.set(location.id, vendor.id)
        if (!vendorNames.has(vendor.id)) vendorNames.set(vendor.id, vendor.name)
      }
    }
    const byLabel = (a: PickerOption, b: PickerOption) => a.label.localeCompare(b.label)
    return {
      locationLabels,
      vendorOfLocation,
      vendorNames,
      vendorOptions: (allowed
        ? allowed.vendor_ids.map((id) => ({ id, label: vendorNames.get(id) ?? `Vendor ${id}` }))
        : [...vendorNames].map(([id, label]) => ({ id, label }))).sort(byLabel),
      locationOptions: (allowed
        ? allowed.location_ids.map((id) => ({ id, label: locationLabels.get(id) ?? `Location ${id}` }))
        : [...locationLabels].map(([id, label]) => ({ id, label }))).sort(byLabel),
    }
  }, [locations, allowed])
  const offeredItems = useMemo(() => {
    if (!allowed) return items
    const ids = new Set(allowed.location_ids)
    return items.filter((item) => {
      const location = itemLocationId(item)
      return location != null && ids.has(location)
    })
  }, [items, allowed])
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items])
  const listPrices = useMemo(() => new Map(items.map((item) => [item.id, item.price])), [items])
  const describeItem = useCallback((itemId: number) => {
    const item = itemsById.get(itemId)
    if (!item) return { name: `Item ${itemId}`, location: null }
    const location = itemLocationId(item)
    return { name: item.name, location: location == null ? null : lookups.locationLabels.get(location) ?? null }
  }, [itemsById, lookups])

  // ------------------------------------------------ bodies and checks
  const input = useMemo(() => draftToInput(draft, eventId, timeZone), [draft, eventId, timeZone])
  const inputKey = useMemo(() => JSON.stringify(input), [input])
  const baseInput = useMemo(
    () => (schedule ? draftToInput(initialDraft, eventId, timeZone) : null),
    [schedule, initialDraft, eventId, timeZone],
  )
  // The PATCH body; null when creating.
  const changes = useMemo(() => (baseInput ? changedFields(baseInput, input) : null), [baseInput, input])
  // Only what falls inside the event's dates is charged. The backend saves
  // any window, so this warns rather than blocks.
  const eventDates = useMemo(() => eventDatesFit(input, event, timeZone), [input, event, timeZone])
  const dirty = changes ? Object.keys(changes).length > 0 : JSON.stringify(draft) !== JSON.stringify(initialDraft)

  // On an edit the server checks rule values only when they are sent, so a
  // stored rule that has since gone out of bounds doesn't block a rename.
  const checkDefaultRule = !changes || 'default_rule_type' in changes
  const checkItemRules = !changes || 'items' in changes
  const errors = useMemo(
    () => validateDraft(draft, { units, timeZone, listPrices, requireName: true, checkDefaultRule, checkItemRules }),
    [draft, units, timeZone, listPrices, checkDefaultRule, checkItemRules],
  )
  // A preview is judged as a new draft: every rule is checked, the name isn't.
  const previewErrors = useMemo(
    () => validateDraft(draft, { units, timeZone, listPrices, requireName: false, checkDefaultRule: true, checkItemRules: true }),
    [draft, units, timeZone, listPrices],
  )
  const valid = !hasErrors(errors)
  const previewable = !archived && !hasErrors(previewErrors)

  // ------------------------------------------------ preview
  const { mutateAsync: requestPreview } = useDiscountPreview()
  const [preview, setPreview] = useState<PreviewState | null>(null)
  const previewSeq = useRef(0)
  const previewInFlight = useRef<{ key: string; promise: Promise<DiscountPreviewResponse | null> } | null>(null)
  // The name doesn't price anything, so typing it doesn't re-run the preview.
  // A paused draft is priced as if running, so the preview still shows what it
  // does (and pausing doesn't re-run it).
  const previewBody = useMemo<DiscountPreviewInput>(
    () => ({ ...input, name: '', is_active: true, ...(schedule ? { id: schedule.id } : {}) }),
    [input, schedule],
  )
  const previewKey = useMemo(() => JSON.stringify(previewBody), [previewBody])

  const runPreview = useCallback((body: DiscountPreviewInput, key: string) => {
    // The same draft already being priced (the debounce and a save's check can meet) is asked once.
    if (previewInFlight.current?.key === key) return previewInFlight.current.promise
    // Only the newest request may write: an older answer arriving late is dropped.
    const seq = ++previewSeq.current
    setPreview((current) => ({ key: current?.key ?? '', data: current?.data ?? null, error: current?.error ?? null, loading: true }))
    const promise = requestPreview(body).then(
      (data) => {
        if (seq === previewSeq.current) setPreview({ key, data, error: null, loading: false })
        return data
      },
      (err: unknown) => {
        if (seq === previewSeq.current) {
          setPreview({ key, data: null, error: discountErrorMessage(err, 'The draft could not be priced.'), loading: false })
        }
        return null
      },
    ).finally(() => {
      if (previewInFlight.current?.key === key) previewInFlight.current = null
    })
    previewInFlight.current = { key, promise }
    return promise
  }, [requestPreview])

  // Refresh the preview a moment after the draft settles, while it is complete.
  const previewedKey = preview?.key
  useEffect(() => {
    if (!previewable || previewedKey === previewKey) return
    const timer = setTimeout(() => { void runPreview(previewBody, previewKey) }, PREVIEW_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [previewable, previewedKey, previewKey, previewBody, runPreview])

  // ------------------------------------------------ save
  const create = useCreateDiscountSchedule(eventId)
  const update = useUpdateDiscountSchedule(eventId)
  const [checkingFree, setCheckingFree] = useState(false)
  const [serverErrors, setServerErrors] = useState<{ key: string; errors: ServerErrors } | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const saving = create.isPending || update.isPending || checkingFree
  const canSave = !readOnly && valid && !saving && (!schedule || dirty)

  const save = async () => {
    if (!canSave) return
    setServerErrors(null)
    // Warn when the draft makes an item free: by its own rules, or by the
    // preview of this exact draft (fetched now when the last one is stale).
    let rows = preview?.key === previewKey ? preview.data?.items ?? null : null
    if (!rows && previewable) {
      setCheckingFree(true)
      rows = (await runPreview(previewBody, previewKey))?.items ?? null
      setCheckingFree(false)
    }
    const free = freeItemIds(draft, items, lookups.vendorOfLocation, units, rows)
    if (free.length > 0 && !window.confirm(`This makes ${free.length} item${free.length === 1 ? '' : 's'} free. Save anyway?`)) return
    try {
      const saved = schedule
        ? await update.mutateAsync({ id: schedule.id, data: changes ?? {} })
        : await create.mutateAsync(input)
      onSaved?.(saved)
      onClose()
    } catch (err) {
      setServerErrors({
        key: inputKey,
        errors: mapServerErrors(
          err,
          (itemId) => itemsById.get(itemId)?.name ?? null,
          (e) => discountErrorMessage(e, 'Could not save the schedule.'),
        ),
      })
      bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const requestClose = useCallback(() => {
    if (saving) return
    if (dirty && !readOnly && !window.confirm('Discard your changes to this schedule?')) return
    onClose()
  }, [saving, dirty, readOnly, onClose])

  // ------------------------------------------------ errors on screen
  // Field errors from the server stand until the draft changes; the general one until the next save.
  const serverFields: ServerErrors['fields'] = serverErrors?.key === inputKey ? serverErrors.errors.fields : {}
  const serverGeneral = serverErrors?.errors.general ?? null
  const shown = (slot: TouchSlot) => !!schedule || !!touched[slot]
  const fieldError = (slot: ErrorSlot, local: string | undefined, touchSlot: TouchSlot) =>
    (shown(touchSlot) ? local : undefined) ?? serverFields[slot] ?? null
  const nameError = fieldError('name', errors.name, 'name')
  const defaultRuleError = fieldError('defaultRule', errors.defaultRule, 'defaultRule')

  let readOnlyReason: string | null = null
  if (readOnly && !archived) {
    if (!canEditDiscounts) readOnlyReason = 'Changing it needs the edit-discounts permission.'
    else if (schedule?.created_scope === 'event' && !scope?.event_wide) readOnlyReason = 'An event admin created it, so only an event admin can change it.'
    else if (!event.vendor_can_create_discounts && !scope?.event_wide) readOnlyReason = "This event doesn't let vendor staff change promotions."
    else readOnlyReason = "It also covers vendors, locations or items you can't discount."
  }

  const title = schedule ? (readOnly ? 'Schedule' : 'Edit schedule') : 'New schedule'
  const saveLabel = saving ? (checkingFree ? 'Checking prices…' : 'Saving…') : schedule ? 'Save changes' : 'Create schedule'
  let footerNote: string | null = null
  if (!readOnly) {
    if (!valid) footerNote = `Still needed: ${missingSummary(errors).join(', ')}.`
    else if (schedule && !dirty) footerNote = 'No changes yet.'
  }

  return (
    <ModalFrame
      title={title}
      subtitle={schedule?.name}
      onDismiss={requestClose}
      bodyRef={bodyRef}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {footerNote && <p className="mr-auto text-xs text-neutral-500 dark:text-neutral-400">{footerNote}</p>}
          <Button type="button" variant="outline" onClick={requestClose} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button type="submit" form="schedule-editor-form" disabled={!canSave}>
              {saveLabel}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {archived && (
          <Notice kind="info">
            This schedule was archived on {formatInEventZone(schedule?.archived_at, timeZone, 'MMM d, yyyy')}. An archived schedule can't be edited.
          </Notice>
        )}
        {readOnlyReason && <Notice kind="info">You can view this schedule. {readOnlyReason}</Notice>}
        {serverGeneral && <Notice kind="error">{serverGeneral}</Notice>}
        {(locationsQuery.error || itemsQuery.error) && (
          <Notice kind="warning">
            Could not load the event's {locationsQuery.error ? 'vendors and locations' : 'menu items'}:{' '}
            {discountErrorMessage(locationsQuery.error ?? itemsQuery.error)}
          </Notice>
        )}
        {scopeQuery.error && !readOnly && (
          <Notice kind="warning">
            Could not check which vendors and locations you can discount, so every one is listed:{' '}
            {discountErrorMessage(scopeQuery.error)}
          </Notice>
        )}

        <form
          id="schedule-editor-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          {/* Disabled while saving too: an edit made during the save's price check would be lost. */}
          <fieldset disabled={readOnly || saving} className="min-w-0 space-y-6">
            <Section title="Schedule">
              <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                <div className="min-w-[14rem] flex-1">
                  <label htmlFor="discount-name" className={LABEL}>Name</label>
                  <input
                    id="discount-name"
                    data-autofocus={schedule ? undefined : true}
                    value={draft.name}
                    maxLength={100}
                    autoComplete="off"
                    placeholder="Happy hour"
                    onChange={(e) => patch({ name: e.target.value }, 'name')}
                    className={fieldClass(!!nameError, 'mt-1 w-full')}
                  />
                  <FieldError>{nameError}</FieldError>
                  <p className={cn(HINT, 'mt-1')}>Staff only. Attendees never see it.</p>
                </div>
                <div className="sm:pt-7">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => patch({ isActive: e.target.checked }, 'name')}
                      className="h-4 w-4"
                    />
                    Active
                  </label>
                  <p className={cn(HINT, 'mt-1 max-w-[16rem]')}>
                    {draft.isActive ? 'Discounts during its window.' : 'Paused: keeps its settings and discounts nothing.'}
                  </p>
                </div>
              </div>
            </Section>

            <Section title="Default rule" description={`What every targeted item without its own rule is charged. ${unitsNote(units)}`}>
              <RuleInput
                id="discount-default-rule"
                rule={draft.defaultRule}
                onChange={(defaultRule) => patch({ defaultRule }, 'defaultRule')}
                units={units}
                error={defaultRuleError}
                allowNone
                noneLabel="No default rule"
              />
              {!draft.defaultRule.type && !defaultRuleError && (
                <p className={HINT}>
                  {overridesOnly(draft)
                    ? 'Every item below has its own rule, so none is needed.'
                    : 'Leave it empty only when every item you add has its own rule and no vendor or location is chosen.'}
                </p>
              )}
            </Section>

            <TargetingSection
              draft={draft}
              onChange={(targets) => patch(targets, 'targets')}
              vendorOptions={lookups.vendorOptions}
              locationOptions={lookups.locationOptions}
              vendorLabels={lookups.vendorNames}
              locationLabels={lookups.locationLabels}
              vendorOfLocation={lookups.vendorOfLocation}
              items={items}
              offeredItems={offeredItems}
              scoped={!!allowed}
              itemsLoading={itemsQuery.isLoading || scopeQuery.isLoading}
              locationsLoading={locationsQuery.isLoading || scopeQuery.isLoading}
              units={units}
              targetsError={fieldError('targets', errors.targets, 'targets')}
              itemErrors={shown('targets') ? errors.items : {}}
            />

            <WindowSection
              draft={draft}
              onChange={(window) => patch(window, 'window')}
              timeZone={timeZone}
              eventDates={eventDates}
              errors={{
                window: serverFields.window ?? null,
                endsAt: fieldError('endsAt', errors.endsAt, 'window'),
                days: serverFields.days ?? null,
                dailyTimes: fieldError('dailyTimes', errors.dailyTimes, 'window'),
                endDate: fieldError('endDate', errors.endDate, 'window'),
              }}
            />
          </fieldset>
        </form>

        {!archived && (
          <PreviewPanel
            note={!draft.isActive && (
              <Notice kind="info">Paused: prices shown as they would be if it were running.</Notice>
            )}
            state={preview}
            current={preview?.key === previewKey}
            autoPreview={previewable}
            onRefresh={() => { void runPreview(previewBody, previewKey) }}
            units={units}
            timeZone={timeZone}
            describeItem={describeItem}
            outsideEvent={eventDates?.overlap === 'none'}
          />
        )}
      </div>
    </ModalFrame>
  )
}
